import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, sessionStore } from '@/lib/api';

// Three paths land us here:
//
//   1. Apple JS SDK (most browsers, response_mode=fragment): id_token
//      arrives in window.location.hash. We POST it to /auth/oauth/apple
//      + exchange for our JWT pair.
//
//   2. Apple form_post mode (Safari + private-browsing + Apple's
//      occasional "use form_post" cases): Apple POSTs to
//      api.wydm.app/auth/oauth/apple/callback, which renders a tiny
//      HTML page that postMessage's the pair back to THIS window if
//      we opened it as a popup, OR writes to localStorage + redirects
//      directly to /home or /builder. In the popup case the listener
//      below picks the message up + completes sign-in here.
//
//   3. The other Apple-from-popup variant where Apple sends id_token
//      back via URL hash to the popup, the popup self-closes after
//      passing it to the opener via postMessage — same listener
//      handles it.
//
// Plain page navigation users hit case 2's redirect path directly
// (the form_post HTML page writes localStorage + bounces); they
// never see this React component.

export function AppleCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Verifying with Apple…');

  useEffect(() => {
    // Case 1 — read id_token from the URL hash if present.
    const params = new URLSearchParams(window.location.hash.slice(1));
    const idToken = params.get('id_token');
    if (idToken) {
      api
        .oauthApple({ id_token: idToken })
        .then((pair) => {
          sessionStore.set(pair.access, pair.refresh, pair.user);
          // Incomplete profiles → builder; the builder's Basics + Age
          // steps collect what the old /auth/profile screen used to.
          navigate(pair.user.profile_complete ? '/home' : '/builder');
        })
        .catch((err) => setStatus(err.message ?? 'Sign-in failed.'));
      return;
    }

    // Case 2 / 3 — wait for a postMessage from the popup that handled
    // the form_post-mode callback. The popup's HTML lives at
    // api.wydm.app/auth/oauth/apple/callback (see auth/oauth_apple.go
    // HandleOAuthAppleFormPost). Message shape:
    //   { type: 'wydm.apple.signin', payload: TokenPair }
    setStatus('Waiting for Apple…');
    const onMessage = (e: MessageEvent) => {
      if (!e.data || typeof e.data !== 'object') return;
      if (e.data.type !== 'wydm.apple.signin') return;
      const pair = e.data.payload as { access: string; refresh: string; user: { profile_complete: boolean } };
      if (!pair?.access || !pair?.refresh) {
        setStatus('Apple did not return a valid session.');
        return;
      }
      sessionStore.set(pair.access, pair.refresh, pair.user as never);
      navigate(pair.user.profile_complete ? '/home' : '/builder');
    };
    window.addEventListener('message', onMessage);
    // Time-out the wait — if no message comes in 30s, surface the
    // dead end instead of an indefinite spinner.
    const timer = window.setTimeout(() => {
      setStatus('No id_token returned by Apple. Try signing in again.');
    }, 30_000);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
    };
  }, [navigate]);

  return (
    <main style={{ maxWidth: 440, margin: '0 auto', padding: '120px 24px', textAlign: 'center' }}>
      <p>{status}</p>
    </main>
  );
}
